import React from 'react';

import { Box, getCSSVariable, Link, Text } from '@opendoor/bricks-next';
import type { BoxProps, LinkProps } from '@opendoor/bricks-next';

import { resolveBricksColor } from './styles/ComponentTheme';
import type { IBNComponentThemeConfig } from './styles/ComponentTheme';

import styles from './Buttons.module.css';

export type ICtaStyles = 'Primary' | 'Secondary' | 'Tertiary' | 'Text';

interface LPLinkProps extends LinkProps {
  componentTheme?: any;
  bnTheme?: IBNComponentThemeConfig;
  ctaStyle?: ICtaStyles;
  ctaUrl?: string;
  ctaDisplayText: string;
  onKeyDown?: React.KeyboardEventHandler<HTMLElement>;
  ref?: React.RefObject<HTMLAnchorElement>;
}

interface LPFauxBtnProps extends Omit<BoxProps, 'tag'> {
  componentTheme?: any;
  ctaStyle?: ICtaStyles;
  ctaDisplayText: string;
}

// Tell the button which keys from the component theme to use
// based on the variant name.
const getBtnTheme = (variant: ICtaStyles) => {
  const theme = {
    Primary: {
      bg: 'buttonBg',
      color: 'buttonColor',
      hover: 'buttonHoverBg',
      active: 'buttonActiveBg',
    },
    Secondary: {
      bg: 'secondaryButtonBg',
      color: 'secondaryButtonColor',
      hover: 'secondaryButtonHoverBg',
      active: 'secondaryButtonActiveBg',
    },
    Tertiary: {
      bg: 'tertiaryButtonBg',
      color: 'tertiaryButtonColor',
      hover: 'tertiaryButtonHoverColor',
      active: 'tertiaryButtonActiveColor',
    },
    Text: {
      bg: 'transparent',
      color: 'linkColor',
      hover: 'linkHoverColor',
      active: 'linkColor',
    },
  };

  return {
    bg: theme[variant].bg,
    color: theme[variant].color,
    hover: theme[variant].hover,
    active: theme[variant].active,
  };
};

// Bricks-next token overrides per variant, used when bnTheme is provided.
const getBNBtnTheme = (
  variant: ICtaStyles,
  bnTheme: IBNComponentThemeConfig,
  getColorValue: (token: any) => any,
) => {
  switch (variant) {
    case 'Secondary':
      return {
        bg: getColorValue(bnTheme.contentPrimary),
        color: getColorValue(bnTheme.bgPrimary),
        hover: getColorValue(bnTheme.secondaryBtnHover),
        active: getColorValue(bnTheme.contentPrimary),
      };
    case 'Tertiary':
      return {
        bg: getColorValue(bnTheme.bgPrimaryLight),
        color: getColorValue(bnTheme.contentPrimary),
        hover: getColorValue(bnTheme.tertiaryBtnHover),
        active: getColorValue(bnTheme.bgPrimaryLight),
      };
    case 'Primary':
    default:
      return {
        bg: getColorValue(bnTheme.bgAccentBrand),
        color: getColorValue(bnTheme.buttonLabelPrimary),
        hover: getColorValue(bnTheme.bgHoverAccentBrand),
        active: getColorValue(bnTheme.bgHoverAccentBrand),
      };
  }
};

// TODO: Remove component theme once bnTheme is completely implemented
export const LPLink = ({
  componentTheme,
  bnTheme,
  ctaStyle = 'Primary',
  ctaUrl,
  ctaDisplayText,
  ref,
  ...props
}: LPLinkProps) => {
  const btnTheme = getBtnTheme(ctaStyle);

  const getColorValue = (bnColorToken: any) => getCSSVariable(bnColorToken, 'color');

  const bnBtn = bnTheme ? getBNBtnTheme(ctaStyle, bnTheme, getColorValue) : null;

  const backgroundColor =
    bnBtn?.bg ?? resolveBricksColor(componentTheme?.[btnTheme.bg] ?? 'brandBlue600');
  const color = bnBtn?.color ?? resolveBricksColor(componentTheme?.[btnTheme.color] ?? 'white0');
  const hoverBg = bnBtn?.hover ?? resolveBricksColor(componentTheme?.[btnTheme.hover] ?? 'white0');
  const activeBg =
    bnBtn?.active ?? resolveBricksColor(componentTheme?.[btnTheme.active] ?? 'white0');

  return ctaStyle === 'Text' ? (
    <Link
      color={resolveBricksColor(componentTheme?.linkColor) ?? getColorValue(bnTheme?.linkColor)}
      cursor="pointer"
      display="inline-flex"
      href={ctaUrl}
      size="medium"
      typography="$labelMedium"
      mt="$6"
      ref={ref}
      {...props}
      hoverStyle={{
        color:
          resolveBricksColor(componentTheme?.linkHoverColor) ?? getColorValue(bnTheme?.linkHover),
      }}
      pressStyle={{
        color: resolveBricksColor(componentTheme?.linkColor) ?? getColorValue(bnTheme?.linkHover),
      }}
    >
      {ctaDisplayText}
    </Link>
  ) : (
    <Link
      backgroundColor={backgroundColor}
      borderRadius="$8x"
      className={styles.lpButton}
      color={color}
      cursor="pointer"
      href={ctaUrl}
      display="inline-flex"
      size="medium"
      position="relative"
      py="$4"
      px="$6"
      mt="$6"
      width="auto"
      ref={ref}
      style={{ '--lp-btn-hover-bg': hoverBg } as React.CSSProperties}
      hoverStyle={{
        color,
      }}
      focusVisibleStyle={{
        color,
        backgroundColor: hoverBg,
      }}
      pressStyle={{
        color,
        backgroundColor: activeBg,
      }}
      {...props}
    >
      <Text tag="span" typography="$bodyMedium" color={color} zIndex={5}>
        {ctaDisplayText}
      </Text>
    </Link>
  );
};

export const LPFauxBtn = ({
  componentTheme,
  ctaStyle = 'Primary',
  ctaDisplayText,
  ...props
}: LPFauxBtnProps) => {
  const btnTheme = getBtnTheme(ctaStyle);
  const backgroundColor = resolveBricksColor(
    componentTheme ? componentTheme[btnTheme.bg] : 'brandBlue600',
  );
  const color = resolveBricksColor(componentTheme ? componentTheme[btnTheme.color] : 'white0');
  const hoverBg = resolveBricksColor(componentTheme ? componentTheme[btnTheme.hover] : 'blue700');
  const activeBg = resolveBricksColor(componentTheme ? componentTheme[btnTheme.active] : 'blue700');

  return ctaStyle === 'Text' ? (
    <Text
      tag="span"
      typography="$bodyMedium"
      color={resolveBricksColor(componentTheme.linkColor)}
      cursor="pointer"
      display="inline-flex"
      mt="$8"
      {...props}
      hoverStyle={{
        color: resolveBricksColor(componentTheme.linkHoverColor),
      }}
      pressStyle={{
        color: resolveBricksColor(componentTheme.linkColor),
      }}
    >
      {ctaDisplayText}
    </Text>
  ) : (
    <Box
      tag="span"
      backgroundColor={backgroundColor}
      borderRadius="$8x"
      className={styles.lpButton}
      cursor="pointer"
      display="inline-flex"
      position="relative"
      py="$4"
      px="$6"
      mt="$8"
      width="auto"
      style={{ '--lp-btn-hover-bg': hoverBg } as React.CSSProperties}
      hoverStyle={{
        backgroundColor,
      }}
      focusVisibleStyle={{
        backgroundColor: hoverBg,
      }}
      pressStyle={{
        backgroundColor: activeBg,
      }}
      {...props}
    >
      <Text tag="span" typography="$bodyMedium" color={color} zIndex={5}>
        {ctaDisplayText}
      </Text>
    </Box>
  );
};
